import Vue from 'vue'
import Vuex from 'vuex'
import {
  forEach,
  has,
  reject,
  mergeLeft,
  isEmpty
} from 'ramda'
import axios from 'axios'
import Global from '@/global'
import mqtt from 'mqtt'
import { v4 as uuidv4 } from 'uuid'
import mqttcomm from '@/mqttcomm'
import rtc from '@/rtccomm'
import Cookies from 'js-cookie'
import {
  // platformLogin,
  // platformLogout,
  logoutFromPlatformToken
} from '@/api/user'
import { message, Modal } from 'ant-design-vue'

import {
  SignalingKeepLiveReq,
  SignalingQueryReq,
  SignalingInviteReq,
  SignalingInviteResultRcp,
  SignalingJoinReq
} from '@/pb/protocol_signaling_pb'
import { EMsg } from '@/pb/msg_entity_pb'
import { TContactInfo, Packet } from '@/pb/header_entity_pb'
import { CMD } from '@/pb/protocol_cmd_pb'
import { IMMsgRpcReq } from '@/pb/protocol_msg_pb'
import {
  ESig,
  SigSessionAcceptParam,
  SigSessionRejectParam,
  SigSessionLeaveParam,
} from '@/pb/signaling_param_pb'
import {
  UserLoginReq,
  UserKeepLiveReq,
  UserLogoutNty
} from '@/pb/protocol_login_pb'
import { EDeviceType } from '@/pb/status_entity_pb'
import { MediaEngineConfigInfo } from '@/pb/mediainfo_pb'
import mediaControl from '@/store/modules/mediacontrol'
import collaborator from './modules/collaborator'
import { addMinutes, format } from 'date-fns'

import inviteFriend from './modules/invite-friend'
import notification from './modules/notification'
import coc from './modules/coc'
import oauth from './modules/oauth'
import user from './modules/user'
import sessionAdd from './modules/sessionAdd'
import tag from './modules/tag'

import router from '@/router'
import { applyRole, confirmRole } from '@/api/talking'
import { CACHE_KEY, CROSS_SUBDOMAIN, LANGUAGE, ONE_DAY_MILLSECS, PAGE_NAME, SIGNALTYPEDEFAULTVALUE } from '@/utils/constant'
import { db } from '@/utils/db'

import { userChecker } from '@/utils/user-checker'
import { clearCurrentCache, incrementNavTagCounter, unloadReset } from '@/utils/vue-storage'
import i18n from '@/i18n'
import { isMobile, isMacOS, isSafari } from '@/utils/ua'
import { imageUrlToBase64, imageUrlToObject } from '@/utils/imageUrlToBase64'

Vue.use(Vuex)

let oldBeatTime = new Date()

/**
 * Web版仅实现”协作“，不实现”基于群组的呼叫“
 * 主叫都是对创建好的协作做”开始协作”，提前开始
 *
 */

const defaultSessionInfo = {
  sessionType: ESig.SessionType.VIDEO_CALL,
  source: ESig.SourceType.FROM_CONFERENCE,
  inviterId: '', // 发起人
  srcId: '', // conference id
  roomId: '',
  roomKey: '',
  members: [
    /* {
    TSigMemberInfo, 内含ESig.MemerStatusType as status, [TSigSessionControl] as controls
    包括当前用户
  } */
  ]
}

// 对成员进行排序，已经在房间内的成员优先，然后主持人优先
function sortMembers(a, b) {
  if (a.inRoom && !b.inRoom) {
    return -1
  } else if (b.inRoom && !a.inRoom) {
    return 1
  } else {
    if (a.isHost) {
      return -1
    } else if (b.isHost) {
      return 1
    } else {
      return a.id - b.id
    }
  }
}

// 根据名称模糊搜索协作标签
function getTagByName(name) {
  return axios
    .get(`/hiapigw/app/tag/search?_id=${Global.genNonce(5)}`, {
      params: {
        name
      }
    })
    .then(res => {
      const { code, data } = res.data
      return +code === 0 ? data?.list[0] : null
    })
}

// 创建标签
function createTag(name) {
  return axios
    .post(`/hiapigw/app/tag/create?_id=${Global.genNonce(5)}`, {
      name
    })
    .then(res => {
      const { code, data } = res.data
      return +code === 0 ? data?.tagInfo : null
    })
}

// 缓存本次协作的白板图片
function cacheConferrnceWhiteBoardImage (conferenceId, imageUrl) {
  if (imageUrl && conferenceId) {
    Vue.ss.set(CACHE_KEY.MEMBER_WHITE_BOARD_IMAGE, {
      [conferenceId]: imageUrl
    })
  }
}
function clearConferrnceWhiteBoardImage (conferenceId, imageUrl) {
  Vue.ss.remove(CACHE_KEY.MEMBER_WHITE_BOARD_IMAGE)
}
// 缓存本次协作的白板线条数据
function cacheConferenceWhiteBoardLines (conferenceId, lines) {
  if (lines && conferenceId) {
    const allLines = Vue.ss.get(CACHE_KEY.MEMBER_WHITE_BOARD_LINES) || {}
    allLines[conferenceId] = lines
    Vue.ss.set(CACHE_KEY.MEMBER_WHITE_BOARD_LINES, allLines)
  }
}
// 清除白板线条数据
function clearConferenceWhiteBoardLines () {
  Vue.ss.remove(CACHE_KEY.MEMBER_WHITE_BOARD_LINES)
}

// 缓存本次协作的冻屏线条数据
function cacheConferenceLines (conferenceId, lines) {
  if (lines && conferenceId) {
    Vue.ss.set(CACHE_KEY.MEMBER_LINES, {
      [conferenceId]: lines
    })
  }
}

// 清除冻屏线条数据
function clearConferenceLines () {
  Vue.ss.remove(CACHE_KEY.MEMBER_LINES)
}

// 声明心跳worker
const heartBeaterWorker = new Worker('/worker/heartBeaterWorker.js')
// 处理来自子线程的心跳通知
heartBeaterWorker.onmessage = function (e) {
  console.log('来自worker: ', e.data)
  const message = e.data
  switch (message) {
    case 'user-alive':
      store.dispatch('sendUserHeartBeat')
      break
    case 'session-alive':
      store.dispatch('sendSessionHeartBeat')
      break
  }
}

// 判断https||http
const isHttps = _ => {
  return window.location.protocol === 'https:' ? '1' : ''
}

// 获取当前RTC引擎屏幕共享分辨率
const getScreenResolution = (sessionMediaType, isMacSafari = isMacOS && isSafari) => {
  console.log(`isMacOS: ${isMacOS} isSafari: ${isSafari}`)
  if (sessionMediaType === Global.MediaSource.MediaSAgora) {
    if (isMacSafari) {
      return Global.AGORA_SCREEN_SHARING_CONFIG_720
    } else {
      return Global.AGORA_SCREEN_SHARING_CONFIG_1080
    }
  } else if (sessionMediaType === Global.MediaSource.MediaSLeiartc) {
    if (isMacSafari) {
      return Global.LEIARTC_SCREEN_SHARING_CONFIG_720
    } else {
      return Global.LEIARTC_SCREEN_SHARING_CONFIG_1080
    }
  }
}

const langKey = 'LocalLang'
const store = new Vuex.Store({
  state: {
    lang: '',
    token: '',
    tokenState: false,
    userInfo: {}, /*    {
      "id":"22121",
      "mobile":"13621628961",
      "email":"fei.xie@hiscene.com",
      "name":"谢非",
      "company":"亮风台集团",
      "corpId":"8000001",
      "hasPhoto":0,
      "spell":"Xie|Fei",
      "Token":"",   // !假的!
      "system_notification":""
    }
    */
    serverProvidedConfig: {}, /*    {
      "VideoRecord": true,
      "MQTTCfg": {
          "ServerAddress": "ws://leia-im.aryun.com:30084/mqtt",
          "SubscibeTopic": "/stoc/hileia/srv_deb/8000001/22121",  // c++@mqttInfo.subTopic
          "PublishTopic": "/ctos/hileia/srv_deb/8000001/22121",   // c++@mqttInfo.pubTopic
          "P2PTopicPrefix": "/ptop/hileia/srv_deb/8000001",
          "LoginPasswd": "srv_deb",
          "ClientID": "clt_hileia_22121_deb",
          "SubUserStatusTopic": "/stoc/hileia/srv_deb/userstatus",
          "SubGroupStatusTopic": "/stoc/hileia/srv_deb/groupstatus",
          "SubCorpTopic": "/stoc/hileia/srv_deb/corp/8000001"     // c++@mqttInfo.subTopic2
      },
      "AGORACfg": {
          "AppKey": "d85708c7ba5f46e59528e496db20955b"
      },
      "IMServerCfg": {
          "fileServerAddress": "https://leia.aryun.com"
      }
    }
    */
    status: mqttcomm.MachineStatus.UnloggedIn,
    mediatype: -1, // MediaSource
    mqttClient: null,
    currentSession: null, // 当前会话，不在会话中时为null
    userOption: {
      autoAcceptCall: false
    },
    callTimeoutTimer: null, // 超时未接听的定时器
    sessionMediaType: -1, // 当前通话的媒体（RTC）类型
    sessionMediaConfig: {}, // 当前通话的媒体（RTC）配置，用于生成RTC实例
    sessionMediaClient: null, // 当前通话的RTC实例
    sessionMembers: [], // 房间内成员
    receivedOtherInvitations: {}, // 存储当前通话以外的邀请信息
    linesData: {}, // 冻屏时线条的数据
    whiteBoardLinesData: {}, // 白板时线条的数据
    whiteBoardImage: null, // 白板图片
    someoneApplyRole: null, // 有人申请权限
    /* {
      userId: '111',
      userName: 'xxx',
      role: 2,
      roleName: 'xxx'
    } */
    roleApplicationRejected: false, // 本地用户申请的权限是否被拒绝
    showUnsupportedNotify: false,
    isMqttOffline: false, // mqtt是否已经离线/被服务器判定为退出登录
    localScreenSharingStatus: Global.SCREEN_SHARING_STATUS.off, // 本地屏幕共享的状态
    localScreenSharingWarningVisible: false, // 本地屏幕共享的警告弹窗是否展示
    realtimeMarkList: [], // 存放激光笔的标注数据

    globalSpin: {
      loading: false,
      get tip () { return `${i18n.tc('common.Loading')}...` }
    },
    networkQuality: SIGNALTYPEDEFAULTVALUE
  },
  getters: {
    isIncomingCall: state =>
      state.status === mqttcomm.MachineStatus.IncomingCall,
    sortedMembers (state) {
      // 过滤掉被踢出房间的人
      return state.sessionMembers.filter(member => member.status !== ESig.MemberStatusType.OUTROOM_USER_KICKOUT).sort(sortMembers)
    },
    mediaOnMembers(state) {
      return state.sessionMembers
        .filter(member => member.cameraOn || member.microphoneOn)
        .sort(sortMembers)
    },
    mediaOffMembers(state) {
      return state.sessionMembers
        .filter(member => !member.cameraOn && !member.microphoneOn)
        .sort(sortMembers)
    },
    localUser(state) {
      return (
        state.sessionMembers.find(
          member => +member.id === +state.userInfo.id
        ) || {}
      )
    },
    isLoginState(state){
      return state.tokenState
    },
    isLogin(state) {
      return !!state.token
    },
    bigVideoUser(state) {
      return state.sessionMembers.find(member => member.cameraOn && member.isPlayBigVideo)
    },
    freezeOnUser (state) {
      return state.sessionMembers.find(member => member.freezeOn)
    },
    whiteBoardOnUser (state) {
      return state.sessionMembers.find(member => member.whiteBoardOn)
    },
    localScreenSharingOn (state) {
      return state.localScreenSharingStatus === Global.SCREEN_SHARING_STATUS.on
    },
    localScreenSharingOff (state) {
      return state.localScreenSharingStatus === Global.SCREEN_SHARING_STATUS.off
    },
    localScreenSharingPending (state) {
      return state.localScreenSharingStatus === Global.SCREEN_SHARING_STATUS.pending
    },
    otherScreenSharingOnUser (state) {
      return state.sessionMembers.find(member => member.screenSharingOn && +member.id !== +state.userInfo.id)
    },
    realtimeMarkOnUser (state) {
      return state.sessionMembers.find(member => member.realtimeMarkOn)
    },
    slamMarkOnUser (state) {
      return state.sessionMembers.find(member => member.slamMarkOn)
    },
    MAXROOMMEMBER (state) {
      return state.serverProvidedConfig.MAXROOMMEMBER
    }
  },
  mutations: {
    setTokenState(state) {
      state.tokenState = false
    },
    initLanguage (state) {
      var lang = localStorage.getItem(langKey)
      if (lang) {
        state.lang = lang
      } else {
        state.lang = LANGUAGE.CHINESE
      }
    },
    setLanguage (state, lang) {
      state.lang = lang
      localStorage.setItem(langKey, lang)
    },
    switchStatus(state, newStatus) {
      state.status = newStatus
    },
    initUserInfo(state, all) {
      if (isEmpty(state.userInfo) || isEmpty(state.token)) {
        state.userInfo = all
        state.token = all.token
      }

      state.tokenState = true
      userChecker.setStateUserId(all.id)
      Cookies.set(CACHE_KEY.COOKIE_USER_ID, all.id, { expires: 1, path: '/', domain: CROSS_SUBDOMAIN })
    },
    saveIMConfig(state, config) {
      state.serverProvidedConfig = config
      if (has('AGORACfg', config)) {
        state.mediatype = Global.MediaSource.MediaSAgora
      } else if (has('HisRtcCfg', config)) {
        state.mediatype = Global.MediaSource.MediaSHisrtc
      } else if (has('LeiaCfg', config)) {
        state.mediatype = Global.MediaSource.MediaSLeiartc
      }
    },
    saveMqttClient(state, client) {
      state.mqttClient = client
    },
    stopSessionHeartBeater() {
      heartBeaterWorker.postMessage(['stop-session-alive'])
    },
    stopUserHeartBeater() {
      heartBeaterWorker.postMessage(['stop-user-alive'])
    },
    saveCurrentSession(state, sessionInfo) {
      state.currentSession = mergeLeft(
        sessionInfo,
        state.currentSession || defaultSessionInfo
      )
    },
    clearCurrentSession(state) {
      state.currentSession = null
    },
    clearUserInfo(state) {
      state.userInfo = {}
      userChecker.setStateUserId(null)
    },
    clearServerProvidedConfig(state) {
      state.serverProvidedConfig = {}
    },
    clearToken(state) {
      state.token = ''
    },
    stopMqttClient(state) {
      if (state.mqttClient !== null) {
        state.mqttClient.end()
        state.mqttClient = null
      }
    },
    saveCallTimeoutTimer(state, timer) {
      state.callTimeoutTimer = timer
    },
    resetCallTimeoutTimer(state) {
      if (state.callTimeoutTimer) {
        clearTimeout(state.callTimeoutTimer)
        state.callTimeoutTimer = null
      }
    },
    saveSessionMediaType(state, type) {
      state.sessionMediaType = type
    },
    saveSessionMediaConfig(state, config) {
      state.sessionMediaConfig = config
    },
    saveSessionMediaClient(state, client) {
      state.sessionMediaClient = client
    },
    clearSessionMediaInfo(state) {
      state.sessionMediaType = -1
      state.sessionMediaConfig = {}
      state.sessionMediaClient = null
    },
    saveSessionMembers(state, members) {
      state.sessionMembers = members
    },
    clearSessionMembers(state) {
      state.sessionMembers = []
    },
    addSessionMember(state, member) {
      state.sessionMembers = [...state.sessionMembers, member]
    },
    toggleSessionMembersOfStreamType (state, type) {
      state.sessionMembers = state.sessionMembers.map(item => ({ ...item, streamType: type }))
    },
    updateSessionMembers(state, member) {
      const allMember = [...state.sessionMembers]
      state.sessionMembers = [
        ...allMember.filter(mem => mem.id !== member.id),
        member
      ]
    },
    saveOtherInvitations(state, invitation) {
      state.receivedOtherInvitations[invitation.roomId] = invitation
    },
    deleteOtherInvitations(state, invitation) {
      state.receivedOtherInvitations[invitation.roomId] = null
    },
    setGlobalSpin(state, spinPorps) {
      state.globalSpin = { loading: false, get tip () { return `${i18n.tc('common.Loading')}...` }, ...spinPorps }
    },
    setWhiteBoardImage (state, { imageBase64, imageObject }) {
      imageObject && (state.whiteBoardImage = imageObject)
      imageBase64 && cacheConferrnceWhiteBoardImage(state.currentSession?.srcId, imageBase64)
    },
    removeWhiteBoardImage (state) {
      state.whiteBoardImage = null
      clearConferrnceWhiteBoardImage(state.currentSession?.srcId, null)
    },
    setWhiteBoardLinesData (state, lines) {
      state.whiteBoardLinesData = lines
    },
    updateWhiteBoardLinesData (state) {
      const result = {}
      Object.keys(state.whiteBoardLinesData).forEach(key => {
        result[key] = state.whiteBoardLinesData[key].map(item => ({ ...item, old: true }))
      })
      state.whiteBoardLinesData = result
      cacheConferenceWhiteBoardLines(state.currentSession?.srcId, state.whiteBoardLinesData)
    },
    addWhiteBoardLinesData (state, line) {
      // 增加画线时间，会根据时间顺序来绘制
      line.drawTime = new Date()
      let memberLines = state.whiteBoardLinesData[line.controluserid]
      if (!memberLines) {
        memberLines = []
      }
      memberLines.push(line)
      // 触发响应
      Vue.set(state.whiteBoardLinesData, line.controluserid, memberLines)
      cacheConferenceWhiteBoardLines(state.currentSession?.srcId, state.whiteBoardLinesData)
    },
    deleteWhiteBoardLinesData (state, memberId) {
      const memberLines = state.whiteBoardLinesData[memberId]
      if (memberLines && memberLines.length > 0) {
        if (!memberLines[memberLines.length - 1]?.old) {
          memberLines.pop()
        }
      }
      cacheConferenceWhiteBoardLines(state.currentSession?.srcId, state.whiteBoardLinesData)
    },
    clearWhiteBoardLinesData (state) {
      state.whiteBoardLinesData = {}
      clearConferenceWhiteBoardLines()
    },
    setLinesData (state, lines) {
      state.linesData = lines
    },
    addLinesData (state, line) {
      // 增加画线时间，会根据时间顺序来绘制
      line.drawTime = new Date()
      let memberLines = state.linesData[line.controluserid]
      if (!memberLines) {
        memberLines = []
      }
      memberLines.push(line)
      // 触发响应
      Vue.set(state.linesData, line.controluserid, memberLines)
      cacheConferenceLines(state.currentSession?.srcId, state.linesData)
    },
    deleteLinesData (state, memberId) {
      const memberLines = state.linesData[memberId]
      if (memberLines && memberLines.length > 0) memberLines.pop()
      cacheConferenceLines(state.currentSession?.srcId, state.linesData)
    },
    clearLinesData (state) {
      state.linesData = {}
      cacheConferenceLines(state.currentSession?.srcId, state.linesData)
    },
    setSomeoneApplyRole (state, payload) {
      state.someoneApplyRole = payload
    },
    clearSomeoneApplyRole (state) {
      state.someoneApplyRole = null
    },
    setRoleApplicationRejected (state, isRejected) {
      state.roleApplicationRejected = isRejected
    },
    setShowUnsupportedNotify (state) {
      state.showUnsupportedNotify = true
    },
    resetShowUnsupportedNotify (state) {
      state.showUnsupportedNotify = false
    },
    setMqttOffline (state, offline) {
      state.isMqttOffline = offline
    },
    setLocalScreenSharingStatus (state, status) {
      state.localScreenSharingStatus = status
    },
    setLocalScreenSharingWarningVisible (state, visible) {
      state.localScreenSharingWarningVisible = visible
    },
    addRealtimeMark (state, point) {
      state.realtimeMarkList.push(point)
    },
    clearRealtimeMark (state) {
      state.realtimeMarkList = []
    },
    setNetworkQuality (state, quality) {
      state.networkQuality = quality
    }
  },
  actions: {
    async setWhiteBoardImage ({ commit }, imageUrl) {
      const result = {
        imageObject: null,
        imageBase64: ''
      }
      if (imageUrl.indexOf('data:image/') === 0) {
        result.imageObject = await imageUrlToObject(imageUrl)
      } else {
        const url = imageUrl.indexOf('/') !== 0 ? `/${imageUrl}` : imageUrl
        result.imageBase64 = await imageUrlToBase64(url)
        result.imageObject = await imageUrlToObject(url)
      }
      commit('clearWhiteBoardLinesData')
      commit('setWhiteBoardImage', result)
    },
    recoverUserSession({ dispatch, commit }, userInfo) {
      Vue.ss.set(CACHE_KEY.NAV_TAG_LOGOUT, false)

      incrementNavTagCounter() // 计数器+1

      commit('initUserInfo', userInfo)
      dispatch('getIMConfig')
      dispatch('notification/fetchNotifyList')
      dispatch('notification/fetchSysNotices')
    },
    async doLogin({ dispatch, state }, payload /* {account, password} */) {
      if (state.token) {
        await dispatch('doLogout')
      }
      // const data = await platformLogin(payload) // 云平台登录接口去掉不要了
      const data = await dispatch('user/loginFromPlatformToken', payload)
      data.id = String(data.id) // 修改id类型，兼容多端
      Vue.ls.set(CACHE_KEY.HILEIA_USER_INFO, data, ONE_DAY_MILLSECS)
      await dispatch('recoverUserSession', data)
      return data
    },
    getIMConfig(context) {
      if (isEmpty(context.state.serverProvidedConfig)) {
        return axios
          .get(`/api/im/config?os=web&api-version=1.3.0&ssl=${isHttps()}&_id=${Global.genNonce(5)}`, { headers: { 'server-host': window.location.hostname } })
          .then(response => {
            if (response.data.retCode === 0) {
              // 测试leiaRTC
              // response.data.config = {
              //   ...response.data.config,
              //   LeiaCfg:{
              //     AppId:"5f8fc873ef751d54ff4c84f6",
              //     AppKey:"202010will0728wei",
              //     Address:"https://leiartc-test.hiar.com"
              //   }
              // }

              // v1
              // const configStr = Global.base64Decode(response.data.config)
              // const config = JSON.parse(configStr)

              // v2 有address字段和无address字段，有地址设置云代理，无地址不设置云代理
              // config.AGORACfg?.Address = ''
              // config.AGORACfg.Mode = 3     // udp:3 tcp:5
              const config = response.data.config

              if (!config?.AGORACfg?.Address){
                config.AGORACfg = { ...config.AGORACfg, Address: '' }
              }

              if (config?.AGORACfg?.AppKey){
                config.engine = Global.MediaSource.MediaSAgora
              } else {
                config.engine = Global.MediaSource.MediaSLeiartc
              }

              config.MAXROOMMEMBER = response.data.maxRoomMember

              context.commit('saveIMConfig', config)

              const client = mqtt.connect(config.MQTTCfg.ServerAddress, {
                clientId: config.MQTTCfg.ClientID
              })
              client.on('reconnect', () => {
                console.error('reconnect:  ', format(new Date(), 'HH:mm:ss'))
                // 清空心跳
                context.commit('stopUserHeartBeater')
                // 切换至重连状态
                context.commit('switchStatus', mqttcomm.MachineStatus.UnloggedIn)
              })
              client.on('connect', () => {
                console.log('mqtt server connected', format(new Date(), 'HH:mm:ss'))
                context.commit('saveMqttClient', client)

                // 订阅topic
                client.subscribe(
                  [config.MQTTCfg.SubscibeTopic, config.MQTTCfg.SubCorpTopic, config.MQTTCfg.SubUserStatusTopic],
                  { qos: mqttcomm.QoS.ExactOnce },
                  (err, granted) => {
                    if (err) {
                      console.error('订阅topic失败： ', err.message)
                    }
                    console.log('已订阅topic： ', granted)
                  }
                )
                // mqtt登录
                context.dispatch('mqttLogin')
                // end of mqtt login
              })
              client.on('message', (topic, message) => {
                const packet = Packet.deserializeBinary(message)
                mqttcomm.onMqttMessage(context, topic, packet)
              })
              client.on('disconnect', () => {
                console.error('disconnect:  ', format(new Date(), 'HH:mm:ss'))
                context.commit('stopSessionHeartBeater')
                context.commit('stopUserHeartBeater')
              })
            } else {
              throw response.data
            }
          })
      }
    },
    mqttLogin (context) {
      // mqtt login c++@him_client_engine.cpp:sendLoginReq()
      const mqttLoginRequest = new UserLoginReq([
        /* base: */ [
          /* header: */ mqttcomm.generateBasePacketHeader(context)
        ],
        /* token: */ context.state.token,
        /* info: */ [
          /* devicetype: */ EDeviceType.WEB,
          /* devUDID: */ uuidv4(), // c++@AccountManager.cpp:constructor() ToDo: save devUDID after generated
          /* clientVersion: */ '1301', // c++@LeiaBoxEngine.cpp:sdkVersion()
          /* versioninfo: */ '',
          /* clientIp: */ 0, // c++@him_client_def.h:struct HIMUserLoginInfo
          /* clientInfo: */ navigator.userAgent,
          /* appkey: */ '', // c++中也空缺
          /* appSecret: */ '' // c++中也空缺
        ],
        /* nickname: */ context.state.userInfo.name,
        /* needReconfirm: */ false
      ])
      mqttLoginRequest.cmd = CMD.USER_LOGIN_REQ
      mqttcomm.doPublish(context, mqttLoginRequest)
    },
    startSessionKeepAlive(context) {
      context.dispatch('sendSessionHeartBeat')
      heartBeaterWorker.postMessage(['start-session-alive', Global.sessionKeepAliveInterval])
    },
    sendSessionHeartBeat(context) {
      const keepAliveRequest = new SignalingKeepLiveReq([
        /* base: */ [mqttcomm.generateBasePacketHeader(context)],
        /* roomid: */ context.state.currentSession.roomId,
        /* roomkey: */ context.state.currentSession.roomKey
      ])
      keepAliveRequest.cmd = CMD.SIGNALING_KEEPLIVE_REQ
      mqttcomm.doPublish(context, keepAliveRequest)
    },
    startUserKeepAlive(context) {
      context.dispatch('sendUserHeartBeat')
      heartBeaterWorker.postMessage(['start-user-alive', Global.userKeepAliveInterval])
    },
    sendUserHeartBeat(context) {
      const newDate = new Date()
      console.log('距离上次发送心跳的间隔： ', (newDate - oldBeatTime) / 1000, format(newDate, 'HH:mm:ss'))
      oldBeatTime = newDate
      const keepAliveRequest = new UserKeepLiveReq([
        /* base: */ [mqttcomm.generateBasePacketHeader(context)],
        /* clientOp: */ 0
      ])
      keepAliveRequest.cmd = CMD.USER_KEEPLIVE_REQ
      mqttcomm.doPublish(context, keepAliveRequest)
    },
    async logoutResetOfNotLogout({ dispatch, commit, state }) {
      // 关闭必要服务，切换到静默状态
      if (state.status === mqttcomm.MachineStatus.SessionEstablished) {
        await dispatch('leaveRtcRoom')
      }
      commit('stopUserHeartBeater')
      commit('stopMqttClient')

      dispatch('notification/stopFetchSysNotices')
    },
    async logoutReset({ dispatch, commit, state }, clearCache = true) {
      // 登出时如果处于通话中，则关闭通话
      if (state.status === mqttcomm.MachineStatus.SessionEstablished) {
        await dispatch('leaveRtcRoom')
      }

      Vue.ss.set(CACHE_KEY.NAV_TAG_LOGOUT, true)
      const token = state.token

      if (clearCache) {
        clearCurrentCache()
      } else {
        unloadReset()
      }

      commit('stopUserHeartBeater')
      commit('clearToken')
      commit('clearUserInfo')
      commit('clearServerProvidedConfig')
      commit('stopMqttClient')
      commit('switchStatus', mqttcomm.MachineStatus.UnloggedIn)

      commit('coc/resetState')
      commit('inviteFriend/resetState')
      commit('notification/resetState')
      commit('user/resetState')

      commit('collaborator/resetState')
      commit('collaborator/resetUserStorage')

      dispatch('notification/stopFetchSysNotices')

      if (clearCache) {
        Cookies.remove(CACHE_KEY.COOKIE_USER_ID)
        if (token) {
          await logoutFromPlatformToken(token)
        }
      }
    },
    mqttLogout(context) {
      const msg = new UserLogoutNty([
        /* base: */ [mqttcomm.generateBasePacketHeader(context)]
      ])
      mqttcomm.doPublish(context, msg)
    },
    doLogout(context) {
      context.dispatch('mqttLogout')
      // platformLogout() // 去掉云平台登出
      return context.dispatch('logoutReset')
    },
    acknowledgeInvitation(context, roomInfo) {
      // 先回应已收到邀请
      const inviteRcp = new SignalingInviteResultRcp([
        [mqttcomm.generateBasePacketHeader(context)],
        roomInfo.roomId,
        roomInfo.roomKey,
        context.state.userInfo.id,
        roomInfo.inviterId,
        0,
        '',
        ''
      ])
      inviteRcp.cmd = CMD.SIGNALING_INVITE_RESULT_RCP
      mqttcomm.doPublish(context, inviteRcp)
    },
    acceptCall(context, payload) {
      // 被叫接听 c++@him_sdk_plugin_signaling.cpp:sendAcceptSignaling
      context.commit('resetCallTimeoutTimer')
      const req = new IMMsgRpcReq([
        /* base: */ [mqttcomm.generateBasePacketHeader(context)],
        /* requestid: */ mqttcomm.generateRequestId(),
        /* reservedid: */ context.state.currentSession.roomId,
        /* rpcType: */ EMsg.RPCType.RPC_TYPE_SIGNALING_SESSION_ACCEPT,
        /* encodeType: */ EMsg.EncodeType.PROBUF_FORMAT_ENCODING,
        /* acceptEncType: */ EMsg.EncodeType.PROBUF_FORMAT_ENCODING,
        /* param: */ new SigSessionAcceptParam([
          // SigSessionAcceptParam
          /* roomid: */ context.state.currentSession.roomId,
          /* roomkey: */ context.state.currentSession.roomKey,
          /* requestid: */ '', // 不set默认为空字符串
          /* inviterid: */ context.state.currentSession.inviterId,
          /* inviteeUserId: */ context.state.userInfo.id
        ]).serializeBinary(),
        /* microphoneClose */ payload.microphoneClose
      ])
      req.cmd = CMD.IM_MSG_RPC_REQ
      mqttcomm.doPublish(context, req)
    },
    async rejectCallManual(context) {
      const microphoneClose = await context.dispatch('getMetuTypeSetting')
      // 主动拒接
      context.commit('resetCallTimeoutTimer')
      context.dispatch('sendRejectReq', {
        roomId: context.state.currentSession.roomId,
        roomKey: context.state.currentSession.roomKey,
        inviterId: context.state.currentSession.inviterId,
        userId: context.state.userInfo.id,
        reason: ESig.RejectReason.NO_ANSWERING,
        microphoneClose
      })
      context.commit('clearCurrentSession')
      context.commit('clearSessionMediaInfo')
      context.commit('switchStatus', mqttcomm.MachineStatus.Idle)
    },
    async rejectCallAsBusy(context, roomInfo) {
      const microphoneClose = await context.dispatch('getMetuTypeSetting')
      // 因为当前正忙而拒接：正在通话中、已处于被叫中
      context.dispatch('sendRejectReq', {
        ...roomInfo,
        userId: context.state.userInfo.id,
        reason: ESig.RejectReason.CURRENTLY_BUSY,
        microphoneClose
      })
    },
    sendRejectReq(context, rejectParams) {
      // 发送拒接请求 c++@him_signaling.cpp:him_signaling_reject
      const req = new IMMsgRpcReq([
        /* base: */ [mqttcomm.generateBasePacketHeader(context)],
        /* requestid: */ mqttcomm.generateRequestId(),
        /* reservedid: */ rejectParams.roomId,
        /* rpcType: */ EMsg.RPCType.RPC_TYPE_SIGNALING_SESSION_REJECT,
        /* encodeType: */ EMsg.EncodeType.PROBUF_FORMAT_ENCODING,
        /* acceptEncType: */ EMsg.EncodeType.PROBUF_FORMAT_ENCODING,
        /* param: */ new SigSessionRejectParam([
          // SigSessionRejectParam
          /* roomid: */ rejectParams.roomId,
          /* roomkey: */ rejectParams.roomKey,
          /* requestid: */ '', // 不set默认为空字符串
          /* inviterid: */ rejectParams.inviterId,
          /* inviteeuserId: */ rejectParams.userId,
          /* reason: */ rejectParams.reason, // ESig.RejectReason.NO_ANSWERING || ESig.RejectReason.CURRENTLY_BUSY
          /* offlineEnabled: */ rejectParams.reason ===
            ESig.RejectReason.NO_ANSWERING,
          /* custominfo: */ ''
        ]).serializeBinary(),
        /* microphoneClose */ rejectParams.microphoneClose
      ])
      req.cmd = CMD.IM_MSG_RPC_REQ
      mqttcomm.doPublish(context, req)
    },
    ignoreCall(context) {
      // 不拒绝，不接听，直接关掉来电弹窗
      context.commit('resetCallTimeoutTimer')
      context.commit('clearCurrentSession')
      context.commit('clearSessionMediaInfo')
      context.commit('switchStatus', mqttcomm.MachineStatus.Idle)
    },
    leaveCallPage(context) {
      if (router.currentRoute.name === PAGE_NAME.Talking) {
        let sourceRoute = router.currentRoute.query.from
        // 如果来源页面不是协作列表或者通讯录，则强制修改为协作列表，防止挂断通话跳转到异常页面
        if (![PAGE_NAME.Home, PAGE_NAME.Organ].includes(sourceRoute)) {
          sourceRoute = PAGE_NAME.Home
        }
        router.replace({
          name: sourceRoute
        })
      }
    },
    leaveCall(context, payload) {
      if (!context.state.currentSession) {
        context.dispatch('leaveCallPage')
        return
      }
      // 离开房间
      const req = new IMMsgRpcReq([
        /* base: */ [mqttcomm.generateBasePacketHeader(context)],
        /* requestid: */ mqttcomm.generateRequestId(),
        /* reservedid: */ context.state.currentSession.roomId,
        /* rpcType: */ EMsg.RPCType.RPC_TYPE_SIGNALING_SESSION_LEAVE,
        /* encodeType: */ EMsg.EncodeType.PROBUF_FORMAT_ENCODING,
        /* acceptEncType: */ EMsg.EncodeType.PROBUF_FORMAT_ENCODING,
        /* param: */ new SigSessionLeaveParam([
          // SigSessionLeaveParam
          /* roomid: */ context.state.currentSession.roomId,
          /* roomkey: */ context.state.currentSession.roomKey,
          /* offlineEnabled: */ true,
          /* customInfo: */ ''
        ]).serializeBinary(),
        /* microphoneClose */ payload.microphoneClose
      ])
      req.cmd = CMD.IM_MSG_RPC_REQ
      mqttcomm.doPublish(context, req)
    },
    async createAndStartImmediateConference(context, memberArray) {
      // update: 2021/11/23 原来是一对一，目前支持一对多
      // 快速创建协作，一对一,一对多，例如从通讯录联系人信息中点击 “立即呼叫”
      // 获取快速协作标签
      let tag = await getTagByName(Global.QUICK_CONFERENCE_TAG)
      // 没有该标签则创建
      if (!tag) tag = await createTag(Global.QUICK_CONFERENCE_TAG)
      if (!tag) {
        console.error('快速协作标签创建失败，请重新尝试呼叫')
        return
      }
      // 构造创建协作的参数
      const isSpecialist = await context.dispatch('getLabelAuthAppltSetting')
      const payload = {
        theme: Global.QUICK_CONFERENCE_TAG,
        beginTime: Global.dateAsSecond(addMinutes(new Date(), 15)),
        endTime: Global.dateAsSecond(addMinutes(new Date(), 45)),
        members: [
          {
            corpId: context.state.userInfo.corpId,
            userId: context.state.userInfo.id,
            nickName: context.state.userInfo.name
          },
          ...memberArray,
          // memberArray数据结构：
          // [
          //   {
          //     corpId: corpId1,
          //     userId: userId1,
          //     nickName: name1
          //   },
          //   {
          //     corpId: corpId2,
          //     userId: userId2,
          //     nickName: name2
          //   }
          // ]
        ],
        tags: [
          {
            id: tag.id
          }
        ],
        isSpecialist: !isSpecialist
      }
      // 创建协作
      const conferenceId = await context.dispatch('createConference', payload)
      if (!conferenceId) {
        console.error('创建协作失败，请重新尝试')
      }
      context.dispatch('startConferencePrematurely', conferenceId)
    },
    createConference(context, payload) {
      // 创建协作，http请求
      // 参数示例
      // const payload = {
      //   theme: '快速会议',
      //   beginTime: 1635758506,
      //   endTime: 1635760306,
      //   members: [
      //     {
      //       corpId: '8000001',
      //       userId: '22210',
      //       nickName: '没错我就是谢非博士',
      //     }
      //   ],
      //   files: [],
      //   tags: [
      //     {
      //       id: '617faf9ef99f4734d83c957b'
      //     }
      //   ]
      // }
      return axios
        .post(
          `/hiapigw/app/conference/create?_id=${Global.genNonce(5)}`,
          payload
        )
        .then(response => {
          const { code, data } = response.data
          return +code === 0 ? data?.conferenceId : null
        })
    },
    async startConferencePrematurely(context, conferenceId) {
      const microphoneClose = await context.dispatch('getMetuTypeSetting', undefined, { root: true })
      // 创建协作后，立即开始协作，主叫
      const conferenceInfo = await axios
        .post(`/hiapigw/app/conference/start?_id=${Global.genNonce(5)}`, {
          conferenceId, // c++@ConferenceManager.cpp:startConference
          microphoneClose: !!microphoneClose
        })
        .then(res => {
          const { code, data, msg } = res.data
          if (+code !== 0) {
            message.error(msg || '开启协作失败，请重新尝试')
          }
          return +code === 0 ? data : null
        })
      if (!conferenceInfo) {
        console.error('开启协作失败，请重新尝试')
        return
      }
      context.commit('switchStatus', mqttcomm.MachineStatus.Calling)
      context.commit('saveCurrentSession', {
        roomId: conferenceInfo.roomId,
        roomKey: conferenceInfo.roomKey,
        inviterId: context.state.userInfo.id,
        inviterName: context.state.userInfo.name
      })
      // 发出邀请信令
      context.dispatch('inviteMemberJoinRoom', conferenceInfo)
      // 请求房间和成员的全量状态
      const queryRequest = new SignalingQueryReq([
        /* base: */ [mqttcomm.generateBasePacketHeader(context)],
        /* roomid: */ conferenceInfo.roomId,
        /* roomkey: */ conferenceInfo.roomKey
      ])
      queryRequest.cmd = CMD.SIGNALING_QUERY_REQ
      mqttcomm.doPublish(context, queryRequest)
    },
    // mqtt邀请他人通话
    inviteMemberJoinRoom (context, conferenceInfo) {
      /* conferenceInfo的结构
      {
        roomId: 'xxx',
        roomKey: 'xxx',
        conferenceId: 'xxx',
        members: [
          {
            userId: 'xxx',
            nickName: 'xxx',
            corpId: 'xxx'
          },
          ...
        ]
      }
       */
      const inviteRequest = new SignalingInviteReq([
        // c++@CallManager.cpp:joinWithInvitees
        /* base: */ [mqttcomm.generateBasePacketHeader(context)],
        /* type: */ ESig.SessionType.VIDEO_CALL,
        /* roomid: */ conferenceInfo.roomId,
        /* roomkey: */ conferenceInfo.roomKey,
        /* roomname: */ context.state.userInfo.id,
        /* roomextinfo: */ new TextDecoder().decode(
          new MediaEngineConfigInfo([
            // c++@AccountManager.cpp:getMediaEngineConfigInfo
            // /* mediaType: */ 1,
            /* mediaType: */ context.state.serverProvidedConfig.engine, // 上一个版本传1是声网，现在传递声网和
            context.state.serverProvidedConfig.engine === Global.MediaSource.MediaSAgora ? [
              context.state.serverProvidedConfig?.AGORACfg?.AppKey ?? '',
              context.state.serverProvidedConfig?.AGORACfg?.Address ?? '',
              context.state.serverProvidedConfig?.AGORACfg?.Mode ?? '',
            ] : null,
            context.state.serverProvidedConfig.engine === Global.MediaSource.MediaSHisrtc ? [] : null,
            context.state.serverProvidedConfig.engine === Global.MediaSource.MediaSLeiartc ? [
              context.state.serverProvidedConfig?.LeiaCfg?.AppId ?? '', // 对方是其他引擎，会使用当前用户的leiaRTC信息
              context.state.serverProvidedConfig?.LeiaCfg?.AppKey ?? '',
              [
                context.state.serverProvidedConfig?.LeiaCfg?.Address ?? ''
              ]
            ] : null
          ]).serializeBinary()
        ),
        /* invitername: */ context.state.userInfo.name,
        /* source: */ ESig.SourceType.FROM_CONFERENCE,
        /* srcid: */ conferenceInfo.conferenceId,
        /* requestid: */ conferenceInfo.roomId,
        /* custominfo: */ '',
        /* offlineenabled: */ true
      ])
      forEach(
        member => {
          inviteRequest.addContacts(
            new TContactInfo([
              /* id: */ member.userId,
              /* name: */ member.nickName,
              /* appid: */ member.corpId,
              /* avatarurl: */ '' // 不需要，如果需要必须另外请求 http://192.168.18.200:99/hiapigw/#api-coc-userBasicList
            ])
          )
        },
        reject(
          m => m.userId === context.state.userInfo.id,
          conferenceInfo.members
        )
      )
      inviteRequest.cmd = CMD.SIGNALING_INVITE_REQ
      mqttcomm.doPublish(context, inviteRequest)
    },
    initRtcClient(context, config) {
      let clientParams
      if (config.mediatype === Global.MediaSource.MediaSAgora) {
        context.commit('saveSessionMediaConfig', config.agora)
        clientParams = Global.AGORA_PARAMS
      } else if (config.mediatype === Global.MediaSource.MediaSLeiartc) {
        context.commit('saveSessionMediaConfig', config.leiartc)
        clientParams = Global.LEIARTC_PARAMS
      }
      context.commit('saveSessionMediaType', config.mediatype)

      let rtcClient
      const serverProvidedConfig = context.rootState?.serverProvidedConfig ?? {}
      if (config.mediatype === Global.MediaSource.MediaSAgora) {
        const PROXYSERVERCONFIG = { udp: 3, tcp: 5 } // 声网云代理设置,默认是udp
        const AGORACfg = config.isHost ? serverProvidedConfig?.AGORACfg ?? {} : config?.agora // 当前会议一直使用主持人的声网代理
        rtcClient = rtc.createRtcClient(config.mediatype, clientParams)
        if (AGORACfg?.address){
          rtcClient.startProxyServer(PROXYSERVERCONFIG[AGORACfg?.mode || 'udp'])
        }
      } else if (config.mediatype === Global.MediaSource.MediaSLeiartc) {
        const address = config?.leiartc?.serveraddressList?.join(';') ?? ''
        rtcClient = rtc.createRtcClient(config.mediatype, clientParams, address)
      }
      context.commit('saveSessionMediaClient', rtcClient)

      rtc.bindRtcEvent(
        config.mediatype,
        context.state.sessionMediaClient,
        context
      )
      rtc.bindNetworkQuality(config.mediatype, context.state.sessionMediaClient, s => {
        // downlinkNetworkQuality uplinkNetworkQuality
        context.commit('setNetworkQuality', s.uplinkNetworkQuality)
      })
    },
    joinRtcRoom(context) {
      let roomParams
      if (context.state.sessionMediaType === Global.MediaSource.MediaSAgora) {
        roomParams = {
          appId: context.state.sessionMediaConfig.appkey,
          channel: context.state.currentSession.roomId,
          token: null,
          uid: context.state.userInfo.id
        }
      } else if (context.state.sessionMediaType === Global.MediaSource.MediaSLeiartc){
        roomParams = {
          appId: context.state.sessionMediaConfig.appid,
          appKey: context.state.sessionMediaConfig.appkey,
          channel: context.state.currentSession.roomId,
          token: null,
          uid: context.state.userInfo.id
        }
      } else {
        console.error('新引擎需要设置加入房间的参数')
      }
      return rtc.joinRoom(
        context.state.sessionMediaType,
        context.state.sessionMediaClient,
        roomParams
      )
    },
    leaveRtcRoom(context) {
      return rtc
        .leaveRoom(
          context.state.sessionMediaType,
          context.state.sessionMediaClient,
          context.getters.localUser.rtcUser
        ).catch(err => {
          console.error('离开RTC房间异常', err)
          Modal.warning({
            title: i18n.tc('calling.RTCRoomAbnormal'),
            content: i18n.tc('calling.RefreshPage')
          })
        })
        .finally(() => {
          context.commit('stopSessionHeartBeater')
          context.commit('clearCurrentSession')
          context.commit('clearSessionMediaInfo')
          context.commit('clearSessionMembers')
          context.commit('clearLinesData')

          context.commit('clearWhiteBoardLinesData')
          context.commit('removeWhiteBoardImage')

          if (context.state.status !== mqttcomm.MachineStatus.UnloggedIn) {
            context.commit('switchStatus', mqttcomm.MachineStatus.Idle)
          } else {
            // 如果当前mqtt已离线，则还是切换至离线状态
            context.commit('switchStatus', mqttcomm.MachineStatus.UnloggedIn)
          }
          clearConferenceLines()
          if (router.currentRoute.name === PAGE_NAME.Talking) {
            let sourceRoute = router.currentRoute.query.from
            // 如果来源页面不是协作列表或者通讯录，则强制修改为协作列表，防止挂断通话跳转到异常页面
            if (![PAGE_NAME.Home, PAGE_NAME.Organ].includes(sourceRoute)) {
              sourceRoute = PAGE_NAME.Home
            }
            router.replace({
              name: sourceRoute
            })
          }
        })
    },
    updateSessionMemberMedia(context, member) {
      context.commit('updateSessionMembers', member)
      if (
        member.cameraOn &&
        !member.isPlayBigVideo &&
        member.rtcUser?.videoTrack &&
        !member.rtcUser.videoTrack.isPlaying
      ) {
        Vue.nextTick(() => {
          member.rtcUser.videoTrack.play(
            `video-player-${member.id}`,
            Global.AGORA_VIDEO_CONFIG
          )
        })
      }
    },
    async controlLocalMedia(context, controls) {
      if ('microphoneOn' in controls) {
        rtc.toggleLocalAudio(
          context.state.sessionMediaType,
          context.state.sessionMediaClient,
          controls.microphoneOn,
          context.getters.localUser.rtcUser
        ).catch(() => {
          console.error('控制本地音频失败')
          const modal = Modal.warning({
            okCancel: true,
            keyboard: false,
            okText: i18n.tc('main.Refresh'),
            cancelText: i18n.tc('common.Close'),
            title: i18n.tc('calling.MicrophoneAbnormal'),
            content: i18n.tc('calling.RefreshPage'),
            onOk: _ => {
              window.location.reload()
            },
            onCancel: _ => {
              modal.destroy()
              context.dispatch('controlMicrophone', false)
            }
          })
          // 被控失败，回退
          if (controls.microphoneOn && controls.isControlled) {
            context.dispatch('controlMicrophone', false)
          }
        })
      }
      if ('cameraOn' in controls) {
        // 如果是在屏幕共享的请求中
        if (context.getters.localScreenSharingPending) {
          if (controls.cameraOn) {
            context.commit('setLocalScreenSharingWarningVisible', true)
          } else {
            // 关闭屏幕共享
            context.dispatch('controlLocalScreenSharing', false)
          }
        } else {
          context.dispatch('getResolutionSetting').then(resolution => {
            rtc.toggleLocalVideo(
              context.state.sessionMediaType,
              context.state.sessionMediaClient,
              controls.cameraOn,
              context.getters.localUser.rtcUser,
              context.state.userInfo.id,
              {
                encoderConfig: Global.CAMERA_CONFIG[resolution],
                optimizationMode: 'motion', // 视频黑屏添加参数
                facingMode: isMobile ? 'environment' : 'user'
              }
            ).catch(() => {
              console.error('控制本地视频失败')
              Modal.warning({
                keyboard: false,
                okText: i18n.tc('main.Refresh'),
                title: i18n.tc('calling.CameraAbnormal'),
                content: i18n.tc('calling.RefreshPage'),
                onOk: _ => {
                  window.location.reload()
                }
              })
            })
          })
        }
      }
      if ('speakerOn' in controls) {
        rtc.toggleLocalSpeaker(
          context.state.sessionMediaType,
          controls.speakerOn,
          context.state.sessionMembers
            .filter(
              member =>
                +member.id !== +context.state.userInfo.id && member.microphoneOn
            )
            .map(member => member.rtcUser.audioTrack)
        )
      }
    },
    async controlLocalScreenSharing (context, on) {
      context.dispatch('realControlScreenSharing', on)
      try {
        const config = getScreenResolution(context.state.sessionMediaType)
        console.log('创建RTC引擎屏幕共享参数', config.encoderConfig)
        await rtc.toggleLocalScreenSharing(
          context.state.sessionMediaType,
          context.state.sessionMediaClient,
          on,
          context.getters.localUser.rtcUser,
          config
        )
      } catch (err) {
        if (on) {
          console.error('开启屏幕共享异常： ', err)
          Modal.warning({
            title: i18n.tc('calling.TurnOnScreenSharingException'),
            content: i18n.tc('calling.PleaseCheckSystemAuth')
          })
          context.dispatch('fakeControlScreenSharing', false)
          context.dispatch('controlLocalScreenSharing', false)
        }
      }

      // 一定要在开启屏幕共享功能后再设置本地共享状态，通话界面需要监听此状态并获取屏幕的视频流，如果在开启之前就转换状态，页面就获取不到视频流
      context.commit('setLocalScreenSharingStatus', on ? Global.SCREEN_SHARING_STATUS.on : Global.SCREEN_SHARING_STATUS.off)
    },
    // 从摄像头直接切换至屏幕共享
    switchCameraToScreen (context) {
      // 关闭摄像头
      rtc.toggleLocalVideo(
        context.state.sessionMediaType,
        context.state.sessionMediaClient,
        false,
        context.getters.localUser.rtcUser,
        context.state.userInfo.id,
        {}
      )
      // 开启屏幕共享
      context.dispatch('controlLocalScreenSharing', true)
    },
    // 从屏幕共享直接切换至摄像头
    switchScreenToCamera (context) {
      // 关闭屏幕共享
      context.dispatch('controlLocalScreenSharing', false)
      // 开启摄像头
      context.dispatch('getResolutionSetting').then(resolution => {
        rtc.toggleLocalVideo(
          context.state.sessionMediaType,
          context.state.sessionMediaClient,
          true,
          context.getters.localUser.rtcUser,
          context.state.userInfo.id,
          {
            encoderConfig: Global.CAMERA_CONFIG[resolution],
            optimizationMode: 'motion', // 视频黑屏添加参数
            facingMode: isMobile ? 'environment' : 'user'
          }
        )
      })
    },
    toggleBigVideo(context, payload) {
      context.commit('updateSessionMembers', {
        ...payload.member,
        isPlayBigVideo: payload.isPlay
      })
    },
    joinConference (context, payload) {
      // 主动加入房间，适应以邀请链接直接加入房间的场景
      const queryRequest = new SignalingJoinReq([
        /* base: */ [mqttcomm.generateBasePacketHeader(context)],
        /* roomid: */ payload.roomId,
        /* roomkey: */ payload.roomKey,
        /* source: */ ESig.SourceType.FROM_CONFERENCE,
        /* srcId: */ payload.conferenceId,
        /* customInfo: */ '',
        /* offlineEnabled: */ '',
        [
          context.state.userInfo.id,
          context.state.userInfo.name,
          context.state.userInfo.corpId,
          context.state.userInfo.avatar,
          payload.roles,
          context.state.userInfo.corpName,
        ],
        /* microphoneClose: */ payload.microphoneClose,
      ])
      queryRequest.cmd = CMD.SIGNALING_JOIN_REQ
      mqttcomm.doPublish(context, queryRequest)
    },
    // 本地用户向房主申请权限
    localUserApplyRole (context, roles) {
      const data = {
        conferenceId: context.state.currentSession.srcId,
        members: [
          {
            userId: context.state.userInfo.id,
            roles
          }
        ],
        requestId: mqttcomm.generateRequestId()
      }
      return applyRole(data)
    },
    // 作为房主，通过或者拒绝他人申请的权限
    confirmOtherUserRole (context, isAllow) {
      const data = {
        conferenceId: context.state.currentSession.srcId,
        userId: context.state.someoneApplyRole.userId,
        role: context.state.someoneApplyRole.role,
        isAllow,
        requestId: mqttcomm.generateRequestId()
      }
      context.commit('clearSomeoneApplyRole')
      return confirmRole(data)
    },
    async getResolutionSetting(context){
      const record = await db.userPreferences.get(context.state.userInfo.id)
      return record?.resolution || '720p'
    },
    async getMetuTypeSetting(context){
      const record = await db.userPreferences.get(context.state.userInfo.id)
      return record?.muteType || 0
    },
    async getLanguageSetting(context){
      if (context.state.userInfo.id) {
        const record = await db.userPreferences.get(context.state.userInfo.id)
        return record?.language || LANGUAGE.CHINESE
      }
      return LANGUAGE.CHINESE
    },
    async getLabelAuthAppltSetting(context){
      const record = await db.userPreferences.get(context.state.userInfo.id)
      // eslint-disable-next-line no-prototype-builtins
      return (record && record.hasOwnProperty('labelAuthApply')) ? record.labelAuthApply : true
    },
  },
  modules: {
    mediaControl,
    inviteFriend,
    notification,
    collaborator,
    coc,
    oauth,
    user,
    sessionAdd,
    tag,
  }
})

export default store
